import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Header from "./Header";
import QuoteForm from "./QuoteForm";
import Footer from "./Footer";
import EnquiryModal from "./EnquiryModal";

const HomePage: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Open enquiry modal after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      // Check if modal was already closed in this session
      // const modalClosed = sessionStorage.getItem("enquiryModalClosed");
      // if (!modalClosed) {
      setShowEnquiryModal(true);
      // }
    }, 5000); // 5 seconds delay

    return () => clearTimeout(timer);
  }, []);

  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: servicesRef, inView: servicesInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: statsRef, inView: statsInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: benefitsRef, inView: benefitsInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: techRef, inView: techInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: processRef, inView: processInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: productRef, inView: productInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const services = [
    {
      icon: (
        <svg
          className="w-8 h-8 text-gray-900"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      title: "Web Development",
      description:
        "Custom web applications built with modern frameworks. Responsive, fast, and scalable solutions for your business.",
    },
    {
      icon: (
        <svg
          className="w-8 h-8 text-gray-900"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
          <line x1="12" y1="18" x2="12" y2="18"></line>
        </svg>
      ),
      title: "Mobile App (Hybrid)",
      description:
        "Cross-platform mobile applications using React Native and Flutter. One codebase, multiple platforms.",
    },
    {
      icon: (
        <svg
          className="w-8 h-8 text-gray-900"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="12" rx="2" ry="2"></rect>
          <line x1="2" y1="20" x2="22" y2="20"></line>
          <line x1="12" y1="16" x2="12" y2="20"></line>
        </svg>
      ),
      title: "Desktop Applications",
      description:
        "Native desktop apps for Windows, macOS, and Linux. High-performance applications with native UI.",
    },
    {
      icon: (
        <svg
          className="w-8 h-8 text-gray-900"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect>
          <polyline points="17 2 12 7 7 2"></polyline>
        </svg>
      ),
      title: "Smart TV Apps",
      description:
        "Custom applications for Smart TV platforms including Android TV, Tizen, and webOS.",
    },
    {
      icon: (
        <svg
          className="w-8 h-8 text-gray-900"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
          <path d="M2 17l10 5 10-5"></path>
          <path d="M2 12l10 5 10-5"></path>
        </svg>
      ),
      title: "AI Services",
      description:
        "Machine learning, AI integration, and intelligent automation solutions. Transform your business with cutting-edge AI technology.",
    },
  ];

  const stats = [
    { number: "100+", label: "Projects Delivered" },
    { number: "50+", label: "Happy Clients" },
    { number: "10+", label: "Years Experience" },
    { number: "24/7", label: "Support Available" },
  ];

  const benefits = [
    {
      icon: (
        <svg
          className="w-6 h-6 text-primary-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      ),
      title: "Quality Assurance",
      description:
        "Rigorous testing and quality checks ensure bug-free, production-ready applications.",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-primary-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      title: "Secure & Reliable",
      description:
        "Enterprise-grade security with best practices and industry standards.",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-primary-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      title: "On-Time Delivery",
      description:
        "We respect deadlines and deliver projects on time without compromising quality.",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-primary-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      title: "Expert Team",
      description:
        "Dedicated developers with expertise in latest technologies and frameworks.",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-primary-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="12" y1="2" x2="12" y2="22"></line>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      title: "Cost-Effective",
      description:
        "Competitive pricing with transparent costs and flexible engagement models.",
    },
    {
      icon: (
        <svg
          className="w-6 h-6 text-primary-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path>
        </svg>
      ),
      title: "24/7 Support",
      description:
        "Round-the-clock support to ensure your project runs smoothly at all times.",
    },
  ];

  const technologies = [
    // Frontend & Frameworks
    "React",
    "Node.js",
    "React Native",
    "Flutter",
    "TypeScript",
    "MERN",
    // Smart TV Platforms
    "Tizen",
    "webOS",
    // Databases
    "MySQL",
    "PostgreSQL",
    "MongoDB",
    "Redis",
    "DynamoDB",
    "Firestore",
    // Cloud Platforms
    "Azure",
    "AWS",
    "Firebase",
    // AI & Machine Learning
    "TensorFlow",
    "PyTorch",
    "OpenAI",
    "Machine Learning",
    // Other
    "Python",
    "Java",
    "C++",
    "Electron",
    "Swift",
    "Kotlin",
  ];

  const processSteps = [
    {
      number: "01",
      title: "Discovery & Planning",
      description:
        "We understand your requirements, goals, and create a detailed project plan.",
    },
    {
      number: "02",
      title: "Design & Prototyping",
      description:
        "UI/UX design and interactive prototypes for your approval before development.",
    },
    {
      number: "03",
      title: "Development & Testing",
      description:
        "Agile development with continuous testing and quality assurance.",
    },
    {
      number: "04",
      title: "Deployment & Support",
      description:
        "Smooth deployment and ongoing maintenance with 24/7 support.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Kyros — Professional IT Services | Web, Mobile, Desktop, Smart TV & AI
          Development
        </title>
        <meta
          name="description"
          content="Kyros provides professional IT services including web development, mobile hybrid apps, desktop applications, Smart TV apps, and AI services. Get a custom quote for your project."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://kyros.com" />
        <meta
          property="og:title"
          content="Kyros — Professional IT Services | Web, Mobile, Desktop, Smart TV & AI Development"
        />
        <meta
          property="og:description"
          content="Kyros provides professional IT services including web development, mobile hybrid apps, desktop applications, Smart TV apps, and AI services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kyros.com" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Kyros",
            description:
              "Professional IT services provider specializing in web, mobile, desktop, Smart TV development, and AI services",
            url: "https://kyros.com",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "Sales",
              availableLanguage: "English",
            },
            sameAs: [],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-700">
        <Header />

        {/* Enhanced Hero Section */}
        <section
          ref={heroRef}
          className="relative min-h-screen flex items-center overflow-hidden dark-gradient"
        >
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-primary-900 opacity-90"
              style={{
                transform: `translate(${mousePosition.x * 0.02}px, ${
                  mousePosition.y * 0.02
                }px)`,
              }}
            />
            <div className="absolute inset-0 bg-black/30" />

            {/* Floating Elements */}
            <motion.div
              className="absolute top-20 left-10 w-20 h-20 bg-primary-500/20 rounded-full blur-xl"
              animate={{
                y: [0, -20, 0],
                x: [0, 10, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute top-40 right-20 w-32 h-32 bg-secondary-500/20 rounded-full blur-xl"
              animate={{
                y: [0, 20, 0],
                x: [0, -15, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute bottom-40 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-lg"
              animate={{
                y: [0, -15, 0],
                x: [0, 20, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Grid Pattern */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                `,
                backgroundSize: "50px 50px",
              }}
            />

            {/* Circular Pattern Graphics */}
            <svg
              className="absolute top-20 right-10 w-64 h-64 opacity-5"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="128"
                cy="128"
                r="120"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeDasharray="10,5"
              />
              <circle
                cx="128"
                cy="128"
                r="80"
                fill="none"
                stroke="white"
                strokeWidth="1"
                strokeDasharray="5,5"
              />
              <circle
                cx="128"
                cy="128"
                r="40"
                fill="none"
                stroke="white"
                strokeWidth="1"
              />
            </svg>

            <svg
              className="absolute bottom-20 left-10 w-48 h-48 opacity-5"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polygon
                points="128,20 188,160 68,160"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeDasharray="8,4"
              />
              <polygon
                points="128,60 158,140 98,140"
                fill="none"
                stroke="white"
                strokeWidth="1"
              />
            </svg>

            {/* Wave Pattern */}
            <svg
              className="absolute bottom-0 left-0 w-full h-32 opacity-10"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path
                d="M0,60 C300,120 600,0 900,60 C1050,90 1200,30 1200,30 L1200,120 L0,120 Z"
                fill="white"
              />
            </svg>
          </div>

          <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-white"
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={heroInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20"
                >
                  <svg
                    className="w-4 h-4 text-primary-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                  </svg>
                  <span className="text-sm font-medium">
                    Trusted by 50+ Companies
                  </span>
                </motion.div>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                  Professional IT Services
                  <span className="block gradient-text">for Your Business</span>
                </h1>
                <p className="text-xl sm:text-2xl text-white/90 mb-8 max-w-2xl leading-relaxed">
                  We build custom software solutions across web, mobile,
                  desktop, Smart TV platforms, and AI services. Transform your
                  ideas into reality with cutting-edge technology.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <motion.a
                    href="#quote"
                    className="btn-primary text-lg px-8 py-4 inline-block text-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={heroInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    Get Started — Request a Quote
                  </motion.a>
                  <motion.a
                    href="#services"
                    className="btn-secondary text-lg px-8 py-4 inline-block text-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={heroInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    Explore Services
                  </motion.a>
                </div>
              </motion.div>

              <motion.div
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {/* Animated Background Orbs */}
                <motion.div
                  className="absolute -top-10 -right-10 w-40 h-40 bg-primary-500/20 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <motion.div
                  className="absolute -bottom-10 -left-10 w-48 h-48 bg-secondary-500/15 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.15, 0.3, 0.15],
                  }}
                  transition={{ duration: 5, repeat: Infinity, delay: 1 }}
                />

                {/* Hero Image */}
                <motion.div
                  className="relative rounded-3xl overflow-hidden shadow-2xl bg-white/5 border border-white/10 min-h-[400px]"
                  animate={{
                    y: [0, -10, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  {/* Placeholder shown by default */}
                  {!imageLoaded && (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-400/10 to-secondary-400/10"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="text-center p-8">
                        <svg
                          className="w-24 h-24 mx-auto mb-4 text-white/40"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-white/60 text-sm font-medium mb-2">
                          Image Not Found
                        </p>
                        <p className="text-white/40 text-xs">
                          Using default hero image (hero-services.svg)
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Try to load image - only shown if it loads successfully */}
                  <img
                    src="/hero-services.svg"
                    alt="Professional IT Services"
                    className={`w-full h-auto object-cover ${
                      imageLoaded ? "block" : "hidden"
                    }`}
                    onLoad={() => {
                      setImageLoaded(true);
                    }}
                    onError={() => {
                      // Try fallback image
                      setImageLoaded(false);
                    }}
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section
          ref={statsRef}
          className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white"
        >
          <div className="container-max">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={statsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="text-4xl md:text-5xl font-bold mb-2">
                    {stat.number}
                  </div>
                  <div className="text-white/90 text-sm md:text-base">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section
          id="services"
          ref={servicesRef}
          className="section-padding bg-white bg-animated-dots text-gray-900"
        >
          <div className="container-max">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={servicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                Our Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive IT solutions tailored to your business needs
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  className="group bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section
          ref={benefitsRef}
          className="section-padding bg-gray-50 text-gray-900"
        >
          <div className="container-max">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                Why Choose Us
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We deliver excellence through expertise, innovation, and
                dedication
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section
          ref={techRef}
          className="section-padding bg-white bg-animated-dots text-gray-900"
        >
          <div className="container-max">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={techInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                Technologies We Use
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We work with cutting-edge technologies to build modern, scalable
                solutions
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-4">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  className="px-6 py-3 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={techInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <span className="text-gray-900 font-medium">{tech}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Process Section */}
        <section
          ref={processRef}
          className="section-padding bg-gradient-to-br from-dark-900 via-dark-800 to-dark-700 text-white"
        >
          <div className="container-max">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={processInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                Our Process
              </h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                A proven methodology to deliver exceptional results
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 30 }}
                  animate={processInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  <div className="glass-card p-6 rounded-2xl">
                    <div className="text-5xl font-bold text-primary-400 mb-4 opacity-50">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary-500/50 transform -translate-y-1/2" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Section - Highlighted */}
        <section
          ref={productRef}
          className="section-padding bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white relative overflow-hidden"
        >
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          </div>

          <div className="container-max relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={productInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30 mb-6">
                <span className="text-sm font-medium">Our Product</span>
              </div>
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4">
                Kyros Time Tracker
                <span className="block text-white/95 mt-2">
                  Employee Tracking Made Easy
                </span>
              </h2>
              <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto">
                Our flagship product — comprehensive employee tracking solution
                with desktop app and web portal
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={productInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-white/90">
                      Native desktop app for Windows, macOS, and Linux
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-white/90">
                      Comprehensive web portal for admins and employees
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-white/90">
                      Real-time analytics and insights dashboard
                    </p>
                  </div>
                </div>
                <Link
                  to="/kyros-time-tracker"
                  className="btn-primary text-lg px-8 py-4 inline-block"
                >
                  View Details — Join Waitlist
                </Link>
              </motion.div>

              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 30 }}
                animate={productInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <motion.div
                  className="glass-card p-6 rounded-2xl"
                  animate={{ rotate: [-1, 1, -1] }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <div className="flex items-center mb-4 gap-3">
                    <div className="h-3 w-3 rounded-full bg-red-400" />
                    <div className="h-3 w-3 rounded-full bg-yellow-400" />
                    <div className="h-3 w-3 rounded-full bg-green-400" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-4 text-white">
                      <div className="text-sm opacity-80">Productivity</div>
                      <div className="text-2xl font-bold">87%</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 text-white">
                      <div className="text-sm opacity-80">Focus Time</div>
                      <div className="text-2xl font-bold">6.2h</div>
                    </div>
                    <div className="col-span-2 bg-white/10 rounded-xl p-6 h-40 flex items-center justify-center border border-white/10">
                      <div className="text-white/80">Analytics Dashboard</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold mb-4">
                Ready to Start Your Project?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Get a free quote and consultation. Let's discuss how we can help
                transform your ideas into reality.
              </p>
              <a
                href="#quote"
                className="btn-secondary text-lg px-8 py-4 inline-block bg-white/20 hover:bg-white/30 border-white/30"
              >
                Get Your Free Quote
              </a>
            </motion.div>
          </div>
        </section>

        {/* Quote Form Section */}
        <QuoteForm />
        <Footer />
      </div>

      {/* Enquiry Modal */}
      <EnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => {
          setShowEnquiryModal(false);
          // Remember that user closed the modal in this session
          sessionStorage.setItem("enquiryModalClosed", "true");
        }}
      />
    </>
  );
};

export default HomePage;
